import React, { useState, useEffect } from 'react';
import './InventoryMeds.css';
import inventoryData from '../../Datas/InventoryData';
import MedicineDetailModal from '../MedicineDetailModal/MedicineDetailModal';
import AddToCartIconinventory from '../../assets/logo/customerscart.png';
import RefillIcon from '../../assets/logo/refill.png';
import ViewDetailsIcon from '../../assets/logo/viewdetails.png';

const InventoryMeds = ({ role }) => {
  const [category] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [filteredMeds, setFilteredMeds] = useState(inventoryData);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [isMedicineDetailModalOpen, setIsMedicineDetailModalOpen] = useState(false);const [selectedColorFilter, setSelectedColorFilter] = useState('medicine-status-ok'); // Default to expired filter


  const categories = {
    'Seasonal Diseases': ['fever', 'cough', 'cold'],
    'Cough & Cold': ['cough', 'throat pain', 'cold'],
    'Pain & Fever': ['pain', 'fever'],
    'Antibiotics': ['infection', 'antibiotic'],
    'Allergy': ['allergy', 'itching', 'hives'],
    'Vitamins': ['vitamins', 'nutrition'],
    'Digestive': ['indigestion', 'stomach', 'acid reflux'],
    'Skin Care': ['acne', 'dry skin', 'eczema'],
    'Eye Care': ['eye drops', 'vision', 'dry eyes'],
    'Oral Health': ['toothache', 'gum', 'mouthwash'],
    'Mental Health': ['stress', 'anxiety', 'depression'],
  };

 useEffect(() => {
  filterMeds(category, searchTerm, selectedTags, selectedColorFilter);
}, [category, searchTerm, selectedTags, selectedColorFilter]); // Make sure selectedColorFilter is in the dependencies

  const handleTagSelect = (tag) => {
    setSelectedTags((prevTags) => {
      const newTags = [...prevTags];
      if (newTags.includes(tag)) {
        return newTags.filter((t) => t !== tag);
      } else {
        newTags.push(tag);
        return newTags;
      }
    });
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleRemoveTag = (tag) => {
    setSelectedTags(selectedTags.filter((t) => t !== tag));
  };

  const filterMeds = (category, searchTerm, tags, colorFilter) => {
    let filtered = inventoryData;
  
    if (category) {
      filtered = filtered.filter((med) =>
        med.description.toLowerCase().includes(category.toLowerCase()) ||
        med.tags.some((tag) => tag.toLowerCase().includes(category.toLowerCase()))
      );
    }
  
    if (tags.length > 0) {
      filtered = filtered.filter((med) =>
        tags.every((tag) => med.tags.includes(tag))
      );
    }
  
    if (searchTerm) {
      filtered = filtered.filter((med) =>
        med.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        med.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
  
    if (colorFilter) {
      filtered = filtered.filter((med) => getStockColor(med) === colorFilter);
    }
  
    // Sort the filtered array based on expiry date:
    filtered = filtered.sort((a, b) => {
      const expiryDateA = new Date(a.expiryDate.split('/').reverse().join('-'));
      const expiryDateB = new Date(b.expiryDate.split('/').reverse().join('-'));
  
      const daysUntilExpiryA = getDaysUntilExpiry(a.expiryDate);
      const daysUntilExpiryB = getDaysUntilExpiry(b.expiryDate);
  
      // Expired items go to the bottom
      if (daysUntilExpiryA < 0 && daysUntilExpiryB < 0) {
        return expiryDateA - expiryDateB; // Sort expired items by closest expiry date (more recent first)
      } else if (daysUntilExpiryA < 0) {
        return 1; // Expired items go to the bottom
      } else if (daysUntilExpiryB < 0) {
        return -1; // Expired items go to the bottom
      }
  
      return daysUntilExpiryA - daysUntilExpiryB; // Sort by days left, soonest at the top
    });
  
    setFilteredMeds(filtered);
  };

  const getDaysUntilExpiry = (expiryDate) => {
    const currentDate = new Date();
    const expiry = new Date(expiryDate.split('/').reverse().join('-'));
    const diffTime = expiry - currentDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatExpiryDate = (expiryDate) => {
    const daysUntilExpiry = getDaysUntilExpiry(expiryDate);
    const status = getStockColor({ expiryDate }); // Get the status (color) for the medicine
  
    if (status === 'medicine-status-expired' || status === 'medicine-status-expiring-soon') {
      if (daysUntilExpiry < 0) {
        return `EXPIRED`; // Just return "EXPIRED" for expired medicines
      }
      return `${daysUntilExpiry} days left`; // Show days left for expiring soon medicines
    }
  
    return `${expiryDate}`; // For others, just show the expiry date without the days
  };
  


  const handleViewDetails = (medicine) => {
    setSelectedMedicine(medicine);
    setIsMedicineDetailModalOpen(true);
  };

  const closeModal = () => {
    setIsMedicineDetailModalOpen(false);
    setSelectedMedicine(null);
  };

  const getStockColor = (med) => {
    const daysUntilExpiry = getDaysUntilExpiry(med.expiryDate);
    const isExpired = daysUntilExpiry <= 0;

    if (isExpired) {
      return 'medicine-status-expired';
    }else if (daysUntilExpiry <= 100) {
      return 'medicine-status-expiring-soon';
    } else if (med.tablets <= 100) {
      return 'medicine-status-low-stock';
    }  else {
      return 'medicine-status-ok';
    }
  };

  const handleShowAll = () => {
    setSelectedColorFilter(''); // Reset to show all
  };
  

  return (
    <div className="inventory-container">
      <div className="header">
        <div className="category-dropdown">
          <button
            className="dropdown-button"
            onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
          >
            {category || 'Select Category'}
          </button>
          {categoryDropdownOpen && (
            <div className="dropdown-content">
              <div className="category-panel">
                {Object.keys(categories).map((categoryName) => (
                  <div className="category-section" key={categoryName}>
                    <h3>{categoryName}</h3>
                    <div className="tags-container">
                      {categories[categoryName].map((tag, index) => (
                        <button
                          key={index}
                          className={`tag-button ${selectedTags.includes(tag) ? 'selected' : ''}`}
                          onClick={() => handleTagSelect(tag)}
                        >
                          {tag}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {role === 'distributor' && (
          <div className="pharmacy-actions">
            <button className="add-med-button-inventory">Add Med</button>
          </div>
        )}

        <div className="search-bar-inventory">
          <input
            type="text"
            placeholder="Search medicines..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className="selected-tags">
        {selectedTags.map((tag, index) => (
          <span key={index} className="tag-capsule">
            {tag}
            <button className="tag-remove" onClick={() => handleRemoveTag(tag)}>&times;</button>
          </span>
        ))}
      </div>

      <div className="color-filter-container">
        <button
          className={`color-filter-button ${selectedColorFilter === 'medicine-status-expired' ? 'selected' : ''}`}
          onClick={() => setSelectedColorFilter('medicine-status-expired')}
        >
          Expired
        </button>
        <button
          className={`color-filter-button ${selectedColorFilter === 'medicine-status-low-stock' ? 'selected' : ''}`}
          onClick={() => setSelectedColorFilter('medicine-status-low-stock')}
        >
          Low Stock
        </button>
        <button
          className={`color-filter-button ${selectedColorFilter === 'medicine-status-expiring-soon' ? 'selected' : ''}`}
          onClick={() => setSelectedColorFilter('medicine-status-expiring-soon')}
        >
          Expiring Soon
        </button>
        <button
          className={`color-filter-button ${selectedColorFilter === 'medicine-status-ok' ? 'selected' : ''}`}
          onClick={() => setSelectedColorFilter('medicine-status-ok')}
        >
          Filled
        </button>
        <button
  className={`color-filter-button ${selectedColorFilter === '' ? 'selected' : ''}`}  // This ensures Show All is selected by default
  onClick={handleShowAll}
>
  Show All
</button>

      </div>

      <table className="inventory-table">
        <thead>
          <tr>
            <th>Status</th>
            <th>Batch Number</th>
            <th>Medicine Name</th>
            <th>Expiry Date</th>
            <th>Stock (Tablets)</th>
            <th>Tags</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredMeds.map((med, index) => {
            const rowClass = getStockColor(med);

            return (
              <tr key={index} className={rowClass}>
                <td className="status-dot">
                  <span className={`dot ${getStockColor(med)}`}></span>
                </td>
                <td>{med.batchNumber}</td>
                <td>{med.name}</td>
                <td>{formatExpiryDate(med.expiryDate)}</td>
                <td>{med.tablets}</td>
                <td>
                  {med.tags.map((tag, i) => (
                    <span key={i} className="tag-capsuless">
                      {tag}
                    </span>
                  ))}
                </td>
                <td>
                  {role === 'distributor' ? (
                    <button className="add-to-cart-button-inventory3">
                      <img src={AddToCartIconinventory} alt="Add to Cart" className="icon-inventory" />
                    </button>
                  ) : (
                    <button className="add-to-cart-button-inventory3">
                      <img src={AddToCartIconinventory} alt="Add to Cart" className="icon-inventory" />
                    </button>
                  )}
                  <button onClick={() => handleViewDetails(med)}>
                    <img src={ViewDetailsIcon} alt="View Details" className="icon-inventory" />
                  </button>
                  {role === 'pharmacist' && (
                    <button className="refill-button">
                      <img src={RefillIcon} alt="Refill" className="icon-inventory" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {isMedicineDetailModalOpen && (
        <MedicineDetailModal
          medicine={selectedMedicine}
          closeModal={closeModal}
        />
      )}
    </div>
  );
};

export default InventoryMeds;
